import ChatMessage from '../../ChatMessage/ChatMessage';
import { RefObject, useMemo, useRef, useEffect, useState } from 'react';
import { LoadingAnimation } from '../../../Types';
import { useSelector } from 'react-redux';
import { DLPState } from '../../../Types/Enums';
import { RootState } from '../../../Store/Reducers';

interface MessageContainerProps {
    messages: Record<string, any>;
    assistantName: string;
    assistantImage: string;
    dlpActive: boolean;
    sliceMessages: Function;
    socket: any;
    messagesEndRef: RefObject<HTMLDivElement | null>;
    loadingAnimation: LoadingAnimation;
    isPreviewPrompt?: boolean;
    dlpState: DLPState;
    chatId: string;
}

export default function MessageContainer({
    messages,
    assistantName,
    assistantImage,
    dlpActive,
    sliceMessages,
    socket,
    messagesEndRef,
    loadingAnimation,
    isPreviewPrompt = false,
    dlpState = DLPState.dlp_optional,
    chatId,
}: MessageContainerProps) {
    const chatContainerStyles = useSelector((state: RootState) => state.chats.chatContainerStyles);
    const isSending = useSelector((state: RootState) => state.chats.isSending);

    const localIsSending = useRef(isSending);
    const chatIdRef = useRef(chatId);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const wasSending = useRef<boolean>(isSending ? true : false);
    const curMessageIds = useRef(new Set<string>());
    const shouldScroll = useRef(false);

    useEffect(() => {
        if (chatIdRef.current !== chatId) {
            wasSending.current = false;
            chatIdRef.current = chatId;
            curMessageIds.current.clear();
        }
    }, [chatId]);

    useEffect(() => {
        if (Object.keys(messages).length > 0) {
            const currentMessageIds = Object.keys(messages);
            const newMessageIds = currentMessageIds.filter((id) => !curMessageIds.current.has(id));

            newMessageIds.forEach((id) => curMessageIds.current.add(id));
            const hasNewMessage = newMessageIds.length > 0;

            if (hasNewMessage || shouldScroll.current) {
                shouldScroll.current = false;

                if (messageContainerRef.current && !wasSending.current) {
                    // First scroll
                    messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
                    if (!wasSending.current) localIsSending.current = false;
                } else {
                    // New Message Smooth Scroll
                    messagesEndRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                    });
                }
            }
        }
    }, [messages, messagesEndRef, isSending]);

    useEffect(() => {
        if (isSending) {
            wasSending.current = true;
            shouldScroll.current = true;
        }
    }, [isSending, messagesEndRef]);

    const renderedMessages = useMemo(
        () =>
            Object.keys(messages).length > 0
                ? Object.entries(messages).map(([key, message], index) => {
                      const isLastMessage = index + 1 === Object.keys(messages).length;
                      const shouldGrow = isLastMessage && wasSending.current && Object.keys(messages).length > 2;

                      return (
                          <>
                              {index === 0 && <div className="h-[35px]" />}
                              <ChatMessage
                                  key={index}
                                  identifier={message.identifier}
                                  assistantName={assistantName}
                                  assistantImage={assistantImage}
                                  role={message.role}
                                  isLastMessage={index + 1 === Object.keys(messages).length}
                                  content={message.content}
                                  originalContent={message.originalContent || ''}
                                  images={message.images}
                                  files={message.files}
                                  contentChanged={message.contentChanged}
                                  dlpActive={dlpActive}
                                  sliceMessages={sliceMessages}
                                  socket={socket}
                                  loadingAnimation={loadingAnimation}
                                  isPreviewPrompt={isPreviewPrompt}
                                  dlpState={dlpState}
                                  sources={message.sources}
                                  isSending={isSending}
                                  extraHeight={0}
                                  shouldGrow={shouldGrow}
                                  chatId={chatId}
                              />
                          </>
                      );
                  })
                : null,
        [
            messages,
            assistantName,
            assistantImage,
            dlpActive,
            sliceMessages,
            socket,
            loadingAnimation,
            isPreviewPrompt,
            dlpState,
            isSending,
            chatId,
        ],
    );

    return (
        <div
            ref={messageContainerRef}
            className={`w-full overflow-y-auto grow ${!isPreviewPrompt ? 'mt-[70px] xl:mt-[100px]' : ''}`}
        >
            <div className={`container mx-auto flex flex-col min-h-full`} style={chatContainerStyles}>
                {<>{renderedMessages}</>}

                <div className={'message max-w-full py-4 px-8'}>
                    <div>
                        <div className="chat-message-container"></div>
                    </div>
                </div>
                <div className="h-3" ref={messagesEndRef} />
            </div>
        </div>
    );
}
